// Browser-side auth client. Exchanges email + password for a signed bearer token
// via the API server (`POST /api/auth/login`) and persists that token in
// localStorage so a refresh keeps the session. The tournament `apiClient` reads
// the stored token to authorize its requests.
//
// The base URL mirrors `apiClient.ts`: `/api` by default (proxied to the server
// in dev), overridable with `VITE_API_BASE_URL` for split deployments.

import {
  ATHLETE_CLAIM_ROUTE,
  ATHLETE_CLAIM_TOKEN_PARAM,
  LOGIN_LINK_ROUTE,
  LOGIN_TOKEN_PARAM,
} from '../domain';
import type { AthleteAccessRelation } from '../domain';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

const TOKEN_STORAGE_KEY = 'tae.authToken';

interface LoginResponse {
  token: string;
}

/** Raised when the API rejects a request for missing/invalid authentication. */
export class UnauthorizedError extends Error {
  constructor(message = 'Authentication required.') {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

// A single handler the auth layer registers so that *any* API call which comes
// back 401 (an expired token, or a user blocked / deleted mid-session) drops
// the whole app to the login screen — not just the `/auth/me` refresh. Without
// this, a blocked user's clicks would each surface a bare "Authentication
// required." alert while the app stayed mounted on a dead session.
let unauthorizedHandler: (() => void) | null = null;

/** Register (or clear with null) the callback invoked on any 401 response. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/** Invoke the registered unauthorized handler, if any. */
export function notifyUnauthorized(): void {
  unauthorizedHandler?.();
}

/** Read the persisted bearer token, or null when not logged in. */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

/** Persist the bearer token for subsequent API calls. */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

/** Remove the persisted bearer token (logout). */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

/**
 * Authenticate with email + password and return a signed bearer token. Throws
 * `UnauthorizedError` on bad credentials and a generic `Error` for other
 * failures, with the server-provided detail when available.
 */
export async function login(email: string, password: string): Promise<string> {
  const res = await fetch(`${BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  if (res.status === 401) {
    const detail = await readErrorDetail(res);
    throw new UnauthorizedError(detail || 'Invalid email or password.');
  }
  if (!res.ok) {
    const detail = await readErrorDetail(res);
    throw new Error(detail || `Login failed: ${res.status} ${res.statusText}`);
  }

  const body = (await res.json()) as LoginResponse;
  return body.token;
}

/**
 * Request a passwordless login link for `email`. The server always answers 202
 * whether or not the address belongs to an account (so this cannot enumerate
 * users); it only rejects when messaging is unconfigured or delivery fails.
 * Throws a generic `Error` (with the server detail) in those cases.
 */
export async function requestLoginLink(email: string): Promise<void> {
  const res = await fetch(`${BASE_URL}/auth/request-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const detail = await readErrorDetail(res);
    throw new Error(detail || `Could not send the login link: ${res.status}`);
  }
}

/**
 * Redeem a passwordless-login token for a signed bearer token. Throws
 * `Error` (with the server detail) on an invalid, expired or already-used link.
 */
export async function verifyLoginLink(token: string): Promise<string> {
  const res = await fetch(`${BASE_URL}/auth/verify-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const detail = await readErrorDetail(res);
    throw new Error(detail || 'This login link is invalid or has expired.');
  }
  const body = (await res.json()) as LoginResponse;
  return body.token;
}

/**
 * The one-time login token carried by an emailed magic link, when the current
 * page is the verify route (`/login?token=…`); null otherwise. Read once at
 * startup so the app can redeem it before showing anything else.
 */
export function consumeLoginTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    if (url.pathname !== LOGIN_LINK_ROUTE) return null;
    const token = url.searchParams.get(LOGIN_TOKEN_PARAM);
    return token && token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

/**
 * Strip the login route + token from the address bar (without reloading), so a
 * refresh never re-submits a spent token and the URL is clean after sign-in.
 */
export function clearLoginTokenFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    window.history.replaceState(null, '', '/');
  } catch {
    // History API unavailable (rare); the stale URL is harmless — the token is
    // single-use and short-lived.
  }
}

/** What claiming an athlete invitation yields: a live session and where to land. */
export interface ClaimResponse {
  token: string;
  personalNumber: string;
  relation: AthleteAccessRelation;
}

/**
 * Claim an athlete-access invitation, returning a signed bearer token plus the
 * profile the new session reaches. `password` is optional: without one the
 * account is passwordless and enters through the magic-link flow instead.
 *
 * Throws `Error` (with the server detail) on an invalid, expired or
 * already-claimed invitation — deliberately indistinguishable, like a spent
 * login link.
 */
export async function claimAthleteInvite(
  token: string,
  password?: string,
): Promise<ClaimResponse> {
  const res = await fetch(`${BASE_URL}/public/athlete-invites/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(password ? { token, password } : { token }),
  });
  if (!res.ok) {
    const detail = await readErrorDetail(res);
    throw new Error(detail || 'This invitation is invalid or has expired.');
  }
  return (await res.json()) as ClaimResponse;
}

/**
 * The one-time claim token carried by an emailed invitation, when the current
 * page is the claim route (`/claim?token=…`); null otherwise. Read at startup so
 * the app can show the claim screen instead of the login screen — but redeemed
 * only on an explicit click, so an email link-scanner cannot burn the
 * single-use token before the recipient gets to it.
 */
export function readClaimTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    if (url.pathname !== ATHLETE_CLAIM_ROUTE) return null;
    const token = url.searchParams.get(ATHLETE_CLAIM_TOKEN_PARAM);
    return token && token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

/** Strip the claim route + token from the address bar after a successful claim. */
export function clearClaimTokenFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    window.history.replaceState(null, '', '/');
  } catch {
    // History API unavailable (rare); harmless — the token is single-use.
  }
}

async function readErrorDetail(res: Response): Promise<string> {
  try {
    return ((await res.json()) as { error?: string }).error ?? '';
  } catch {
    return '';
  }
}
